// Whole-file SHA-256. WebCrypto cannot hash incrementally, so the bytes go through the pure-JS hasher,
// in a dedicated worker when the platform allows it (keeps the page responsive on large files).
import { Sha256 } from './sha256.js';

/** Inline hasher; yields to the event loop every few MiB. */
export function createInlineHasher() {
  const h = new Sha256();
  let sinceYield = 0;
  return {
    async update(bytes) {
      h.update(bytes);
      sinceYield += bytes.length;
      if (sinceYield >= 4 * 1024 * 1024) { sinceYield = 0; await new Promise((r) => setTimeout(r, 0)); }
    },
    async digest() { return h.digestHex(); },
    close() {},
  };
}

/** Worker-backed hasher. Bytes are transferred (the caller must not reuse the buffer). */
export function createWorkerHasher() {
  const w = new Worker(new URL('./file-hash-worker.js', import.meta.url), { type: 'module' });
  let pending = null;
  w.onmessage = (e) => { const p = pending; pending = null; if (p) p.resolve(e.data); };
  w.onerror = (e) => { const p = pending; pending = null; if (p) p.reject(new Error(e.message || 'hash worker failed')); };
  const ask = (msg, transfer) => new Promise((resolve, reject) => { pending = { resolve, reject }; w.postMessage(msg, transfer || []); });
  return {
    async update(bytes) {
      const buf = bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength ? bytes.buffer : bytes.slice().buffer;
      await ask({ op: 'update', buf }, [buf]);
    },
    async digest() { const r = await ask({ op: 'digest' }); return r.hex; },
    close() { w.terminate(); },
  };
}

export function defaultFileHasher() {
  if (typeof Worker === 'function' && typeof window !== 'undefined') {
    try { return createWorkerHasher(); } catch { /* fall through */ }
  }
  return createInlineHasher();
}
